import { PalmettoLoader } from '@palmetto/dsm-components';
import classNames from 'classnames';
import React, { useContext } from 'react';

import { useDebug } from '@helios/frontend-project';
import { PlatformConfig } from '../../config';

const LoadingPage: React.FC = () => (
  <div
    role="status"
    className={classNames('inset-0 flex items-center justify-center', PlatformConfig.isChromatic ? 'absolute' : 'fixed')}
  >
    <div className="absolute inset-0 transform-gpu bg-black/30" />
    <PalmettoLoader size="lg" className="relative" />
  </div>
);

const LoadingPageContext = React.createContext<{
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}>({
  isOpen: false,
  setIsOpen: () => {
    /* no-op */
  },
});

const GlobalLoadingPage: React.FC = () => {
  const { isOpen } = useContext(LoadingPageContext);
  const debug = useDebug();
  if (isOpen) {
    debug('render GlobalLoadingPage as LoadingPage');
    return <LoadingPage />;
  }
  debug('render GlobalLoadingPage as null');
  return null;
};

interface LoadingPageProviderProps {
  children: React.ReactNode;
}

export const LoadingPageProvider: React.FC<LoadingPageProviderProps> = ({ children }) => {
  const [isOpen, setIsOpen] = React.useState<boolean>(false);

  const timerRef = React.useRef<number | NodeJS.Timeout | null>(null);
  const isOpenRef = React.useRef<boolean>(false);

  const debouncedSetIsOpen = React.useCallback((open: boolean) => {
    isOpenRef.current = open;
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
    }
    timerRef.current = window.setTimeout(() => {
      setIsOpen(isOpenRef.current);
    }, 200);

    return () => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);
  const value = React.useMemo(
    () => ({
      isOpen,
      setIsOpen: debouncedSetIsOpen,
    }),
    [debouncedSetIsOpen, isOpen],
  );

  return (
    <LoadingPageContext.Provider value={value}>
      {children}
      <GlobalLoadingPage />
    </LoadingPageContext.Provider>
  );
};

const LocalLoadingPage: React.FC = () => {
  const { setIsOpen } = useContext(LoadingPageContext);

  React.useEffect(() => {
    setIsOpen(true);
    return () => {
      // clear on unmount
      setIsOpen(false);
    };
  }, [setIsOpen]);

  return null;
};

export default React.memo(LocalLoadingPage);
